import { Box, Divider, FormControl, Grid2, MenuItem, Paper, Select, SpeedDial, SpeedDialAction, SpeedDialIcon, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import axios from 'axios';
import { useContext } from "react";
import authService from '../auth/auth.service';
import LoadingIndicator from "../common/loading.indicator";
import { DialogsContext, SessionDataContext } from "../contexts";
import { refreshAccessToken } from "../http-common";
import { schoolApi } from "../school/school.queries";
import StudentCard from "../student/student.card";
import { DarkModeSwitch } from "./darkMode.switch";
import UserAvatar from './user.avatar';
import { userApi } from "./user.queries";

export default function UserView(props) {
	const useGet = userApi.useGet('me');
	const user = useGet?.data;
	const useSetPreference = userApi.useSetPreference();
	const usePreUpload = userApi.usePreUpload();
	const usePatch = userApi.usePatch();

	const useGetAll = schoolApi.useGetAll();
	let schools = useGetAll?.data;

	const { dialogs } = useContext(DialogsContext);
	const sessionData = useContext(SessionDataContext);

	if (useGetAll?.isPending) {
		return <LoadingIndicator />;
	}

	if (!schools?.length) {
		window.location.href = '/wizard';
		return;
	}

	const school = schools?.[0];

	if (!user || !school) {
		return;
	}

	user.prefs.mode ??= 'light';
	user.prefs.weeklyIncludeWeekends ??= '0';
	user.prefs.weeklyStartsOn ??= '0';
	user.prefs.monthlyIncludeWeekends ??= '0';
	user.prefs.monthlyStartsOn ??= '0';

	const setPreference = async (key, value) => {
		user.prefs[key] = value;
		await useSetPreference.mutateAsync({key, value});
		await refreshAccessToken();
	}

	const uploadFile = async (file) => {
		const id = user.id;
		const data = {
			fileName: file.name,
			fileType: file.type,
		};
		let result = await usePreUpload.mutateAsync({id, data});

		const url = result.url;
		const { origin, pathname } = new URL(url);

		result = await axios.put(url, file, {
			headers: {
				'Content-Type': file.type,
			},
		});

		const updated = { id: user.id, profilePicture: origin + pathname };
		usePatch.mutateAsync(updated).then((result) => {
			// setUser(_.assign(user, updated));

			//To refresh the user object
			authService.login('self');
		});
	};

	const stackSx = { justifyContent: 'space-between', alignItems: 'center' };

	return (
		<Box>
			<Paper sx={{ p: 2, flexGrow: 1, flex: 1 }}>
				<Typography variant="h3">{school?.name}</Typography>

				<Grid2 container spacing={4}>
					<Grid2 size={{ xs: 12, sm: 8, md: 9, lg: 10, xl: 10.5 }}>
						<Divider textAlign="left" sx={{ pt: 4, pb: 2 }}>Students</Divider>
						<Grid2 container spacing={4}>
							{school?.students?.map(student =>
								<Grid2 size={{ xs: 12, md: 6, lg: 4, xl: 3 }} key={student.id}>
									<StudentCard student={student} />
								</Grid2>
							)}
						</Grid2>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 2, xl: 1.5 }}>
						<Divider textAlign="left" sx={{ pt: 4, pb: 2 }}>Users</Divider>
						<Stack direction="column">
							{school?.users?.map(u =>
								<Stack key={u.id} spacing={1} alignItems="center">
									<UserAvatar user={u} />
									<Typography>{u.firstName + ' ' + u.lastName}</Typography>
									{u.id == user.id && <Typography>(you)</Typography>}
								</Stack>
							)}
						</Stack>
						<Divider textAlign="left" sx={{ pt: 4, pb: 2 }}>Preferences</Divider>
						<Stack direction="column">

							<Stack sx={stackSx}>
								<Typography variant="button">Theme</Typography>
								<Stack spacing={1} sx={{ alignItems: 'center' }}>
									<Typography>Light</Typography>
									<DarkModeSwitch checked={user.prefs.mode == 'dark'} onClick={() => setPreference('mode', user.prefs.mode == 'light' ? 'dark' : 'light')} />
									<Typography>Dark</Typography>
								</Stack>
							</Stack>

							{/* <Stack sx={stackSx}>
								<Typography>Focus</Typography>
								<ToggleButtonGroup value={user.prefs.focus} exclusive onChange={(e) => setPreference('focus', e.target.value)}>
									<ToggleButton value="student">Student</ToggleButton>
									<ToggleButton value="course">Course</ToggleButton>
								</ToggleButtonGroup>
							</Stack> */}

							<Typography variant="button">Weekly</Typography>
							<Stack direction="column" gap={1} sx={{ pl: 1, py: 1 }}>
								<Stack sx={stackSx}>
									<Typography>Show Weekends</Typography>
									<ToggleButtonGroup value={user.prefs.weeklyIncludeWeekends} exclusive onChange={(e) => setPreference('weeklyIncludeWeekends', e.target.value)}>
										<ToggleButton value="0">Off</ToggleButton>
										<ToggleButton value="1">On</ToggleButton>
									</ToggleButtonGroup>
								</Stack>

								<Stack sx={stackSx}>
									<Typography>Week Starts On</Typography>
									<FormControl variant="standard">
										<Select value={user.prefs.weeklyStartsOn} onChange={(e) => setPreference('weeklyStartsOn', e.target.value)}>
											<MenuItem value={0}>Sunday</MenuItem>
											<MenuItem value={1}>Monday</MenuItem>
											{/* <MenuItem value={2}>Tuesday</MenuItem>
												<MenuItem value={3}>Wednesday</MenuItem>
												<MenuItem value={4}>Thursday</MenuItem>
												<MenuItem value={5}>Friday</MenuItem>
												<MenuItem value={6}>Saturday</MenuItem> */}
										</Select>
									</FormControl>
								</Stack>
							</Stack>

							<Typography variant="button" sx={{ py: 5 }}>Monthly</Typography>
							<Stack direction="column" gap={1} sx={{ pl: 1, py: 1 }}>
								<Stack sx={stackSx}>
									<Typography>Show Weekends</Typography>
									<ToggleButtonGroup value={user.prefs.monthlyIncludeWeekends} exclusive onChange={(e) => setPreference('monthlyIncludeWeekends', e.target.value)}>
										<ToggleButton value="0">Off</ToggleButton>
										<ToggleButton value="1">On</ToggleButton>
									</ToggleButtonGroup>
								</Stack>
								<Stack sx={stackSx}>
									<Typography>Week Starts On</Typography>
									<FormControl variant="standard">
										<Select value={user.prefs.monthlyStartsOn} onChange={(e) => setPreference('monthlyStartsOn', e.target.value)}>
											<MenuItem value={0}>Sunday</MenuItem>
											<MenuItem value={1}>Monday</MenuItem>
											{/* <MenuItem value={2}>Tuesday</MenuItem>
												<MenuItem value={3}>Wednesday</MenuItem>
												<MenuItem value={4}>Thursday</MenuItem>
												<MenuItem value={5}>Friday</MenuItem>
												<MenuItem value={6}>Saturday</MenuItem> */}
										</Select>
									</FormControl>
								</Stack>
							</Stack>

						</Stack>
					</Grid2>
				</Grid2>
			</Paper>

			<SpeedDial sx={{ position: 'fixed', bottom: 10, right: (sessionData.drawerOpen ? 350 : 40), alignItems: 'end', '.MuiSpeedDial-actions': { alignItems: 'end', '.MuiFab-circular': { borderRadius: 2, width: 'auto' } } }} color="primary" icon={<SpeedDialIcon />} ariaLabel="">
				<SpeedDialAction icon={<Typography sx={{ p: 2 }}>New Student</Typography>} onClick={() => dialogs.open('student', {})} />
				<SpeedDialAction icon={<Typography sx={{ p: 2 }}>Invite User</Typography>} onClick={() => dialogs.open('user', {})} />
			</SpeedDial>
		</Box >
	);
}